import { log, settings } from "@tumbodash/browserPackages";

interface CrashReporterOptions {
	endpoint?: string;
	getContext?: () => Record<string, unknown>;
	sampleRate?: number;
}

interface CrashPayload {
	type: string;
	message: string;
	filename?: string;
	lineno?: number;
	colno?: number;
	stack?: string;
}

export default function crashReporter({
	endpoint = settings.serverUrl + "debug",
	getContext = () => ({}),
	sampleRate = 1,
}: CrashReporterOptions = {}) {
	const sent = new Set<string>();

	function shouldSend() {
		return Math.random() <= sampleRate;
	}

	function safeJson(value: unknown): unknown {
		try {
			return JSON.parse(JSON.stringify(value));
		} catch {
			return String(value);
		}
	}

	async function sendCrash(payload: CrashPayload) {
		if (!shouldSend()) return;

		const key = `${payload.type}|${payload.message}|${payload.stack}`;
		if (sent.has(key)) return;
		sent.add(key);

		const body = {
			...payload,
			context: safeJson(getContext()),
			href: location.href,
			userAgent: navigator.userAgent,
			time: new Date().toISOString(),
			build: import.meta.env.VITE_BUILD_SHA || "unknown",
		};

		try {
			await fetch(endpoint!, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
				credentials: "omit",
				keepalive: true,
			});
		} catch (err) {
			console.error("Crash reporter failed", err);
		}
	}

	window.addEventListener("error", (event) => {
		sendCrash({
			type: "error",
			message: event.message || "Unknown error",
			filename: event.filename,
			lineno: event.lineno,
			colno: event.colno,
			stack: event.error?.stack,
		});
	});

	window.addEventListener("unhandledrejection", (event) => {
		const reason = event.reason as { message?: string; stack?: string } | string;
		sendCrash({
			type: "unhandledrejection",
			message: typeof reason === "object" ? reason?.message ?? String(reason) : String(reason),
			stack: typeof reason === "object" ? reason?.stack : undefined,
		});
	});
}
